import { getRouteApi } from '@tanstack/react-router';
import { useId, useState, type ReactNode } from 'react';
import { AUDIENCE_NAMES, DIALECTS, ENTITY_TYPE_NAMES, PAPERS, type Paper } from '@realmery/realm-core';
import { myDictionary, removeFromMyDictionary, useSpellingVersion } from '../spelling/client.ts';
import { realmKey } from '../db/protocol.ts';
import {
  AUDIENCES,
  DEFAULTS,
  ENTRY_TYPES,
  MAP_GRIDS,
  ON_START_CHOICES,
  START_CHOSEN_REALM,
  TEXT_SIZES,
  UNIT_SYSTEMS,
  WRITING_FONTS,
  allSkins,
  importSkin,
  removeCustomSkin,
  saveSettings,
  useSettings,
  type Settings,
} from '../settings.ts';
import { AUTOMATIC } from '../skins.ts';
import { ShortcutSettings } from './ShortcutSettings.tsx';

const route = getRouteApi('/_app/settings');

/**
 * Settings on this device (docs/PARITY-STEP-1-PLAN.md section 5): the desktop's Settings
 * window, for what applies on the web. Each change is saved and applied at once.
 */
export function SettingsPage() {
  const list = route.useLoaderData();
  const s = useSettings();
  const [notice, setNotice] = useState<{ kind: 'info' | 'error'; text: string } | null>(null);
  const [, rerender] = useState(0);
  const skins = allSkins();

  function change(changes: Partial<Settings>) {
    if (!saveSettings(changes)) setNotice({ kind: 'error', text: "This browser isn't keeping settings, so they'll last until this page closes." });
  }

  async function importFile(file: File | undefined) {
    if (!file) return;
    try {
      const skin = importSkin(await file.text());
      change({ 'appearance.skin': skin.id });
      setNotice({ kind: 'info', text: `Added the skin “${skin.name}”.` });
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof Error ? e.message : 'That skin could not be read.' });
    }
  }

  const chosen = skins.find((k) => k.id === s['appearance.skin']);
  return (
    <article className="document settings-page">
      <p className="eyebrow">Settings</p>
      <h1>Settings</h1>
      <p className="lead">Kept in this browser, on this device. Each change applies at once.</p>
      {notice && (
        <p className={notice.kind === 'error' ? 'notice notice-error' : 'notice'} role={notice.kind === 'error' ? 'alert' : 'status'}>
          {notice.text}
        </p>
      )}

      <Section title="General">
        <Field label="When Realmery starts" hint="Show your realms, reopen the realm you had open last time, or open one realm of your choice.">
          {(ids) => (
            <select {...ids} value={s['general.onStart']} onChange={(e) => change({ 'general.onStart': e.currentTarget.value })}>
              {ON_START_CHOICES.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          )}
        </Field>
        {s['general.onStart'] === START_CHOSEN_REALM && (
          <Field label="Realm to open">
            {(ids) => (
              <select {...ids} value={s['general.startRealm']} onChange={(e) => change({ 'general.startRealm': e.currentTarget.value })}>
                <option value="">Choose a realm</option>
                {list.map((realm) => (
                  <option key={realm.file} value={realmKey(realm.file)}>
                    {realm.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
        <Check
          label="Ask before deleting"
          hint="Ask “Are you sure?” before deleting an entry or a collection. Deleting a realm always asks, since its backup goes too."
          checked={s['general.confirmDeletes']}
          onChange={(checked) => change({ 'general.confirmDeletes': checked })}
        />
      </Section>

      <Section title="Appearance">
        <Field label="Skin" hint={chosen ? chosen.description : 'Forge, or Ember Night when this device is in dark mode.'}>
          {(ids) => (
            <select {...ids} value={s['appearance.skin']} onChange={(e) => change({ 'appearance.skin': e.currentTarget.value })}>
              <option value={AUTOMATIC}>Automatic: light or dark, as this device is</option>
              {skins.map((skin) => (
                <option key={skin.id} value={skin.id}>
                  {skin.name}
                  {skin.builtIn ? '' : ' (your own)'}
                </option>
              ))}
            </select>
          )}
        </Field>
        <div className="actions">
          <label className="button file-button">
            Import a skin file…
            <input type="file" accept=".css,text/css" hidden onChange={(e) => void importFile(e.currentTarget.files?.[0]).then(() => (e.target.value = ''))} />
          </label>
          {chosen && !chosen.builtIn && (
            <button
              type="button"
              className="quiet"
              onClick={() => {
                removeCustomSkin(chosen.id);
                rerender((n) => n + 1);
              }}
            >
              Remove “{chosen.name}”
            </button>
          )}
        </div>
        <p className="meta">
          A skin from Realmery on the desktop: the <code>.css</code> files in <code>.realmery\skins</code> in your user folder.
          Only its colours are used, adjusted where needed so text stays easy to read.
        </p>
        <Field label="Interface text size" hint="The size of menus, buttons, lists, and labels.">
          {(ids) => (
            <select {...ids} value={s['appearance.textSize']} onChange={(e) => change({ 'appearance.textSize': e.currentTarget.value })}>
              {TEXT_SIZES.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Writing font" hint="The font of entry content, while reading and writing. “The usual font” keeps the editor's own.">
          {(ids) => (
            <select {...ids} value={s['appearance.writingFont']} onChange={(e) => change({ 'appearance.writingFont': e.currentTarget.value })}>
              {WRITING_FONTS.map(([id, name]) => (
                <option key={name} value={id}>
                  {name}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Writing text size" hint="In points. 0 keeps the editor's own.">
          {(ids) => (
            <input
              {...ids}
              type="number"
              min={0}
              max={48}
              step={1}
              value={s['appearance.writingSize']}
              onChange={(e) => {
                const value = Math.round(Number(e.currentTarget.value));
                if (Number.isFinite(value)) change({ 'appearance.writingSize': Math.min(48, Math.max(0, value)) });
              }}
            />
          )}
        </Field>
        <Check
          label="Animations"
          hint="Let things slide and fade, rather than appear at once. Also off when this device asks for less motion."
          checked={s['appearance.animations']}
          onChange={(checked) => change({ 'appearance.animations': checked })}
        />
      </Section>

      <Section title="Dice and character sheets">
        <Field label="Rolls kept in the History" hint="How many rolls the Dice Roller's History keeps: 10 to 5,000.">
          {(ids) => (
            <input
              {...ids}
              type="number"
              min={10}
              max={5000}
              step={10}
              value={s['dice.historyLimit']}
              onChange={(e) => {
                const value = Math.round(Number(e.currentTarget.value) / 10) * 10;
                if (Number.isFinite(value)) change({ 'dice.historyLimit': Math.min(5000, Math.max(10, value)) });
              }}
            />
          )}
        </Field>
        <Field label="Paper for printed sheets" hint="The page size a character sheet prints on, and a template's blank sheet.">
          {(ids) => (
            <select {...ids} value={s['print.paperSize']} onChange={(e) => change({ 'print.paperSize': e.currentTarget.value as Paper })}>
              {(Object.keys(PAPERS) as Paper[]).map((paper) => (
                <option key={paper} value={paper}>
                  {PAPERS[paper]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Dice the die buttons roll" hint="How many dice the Dice Roller's d4 to d100 buttons start on.">
          {(ids) => (
            <input
              {...ids}
              type="number"
              min={1}
              max={100}
              value={s['new.diceCount']}
              onChange={(e) => {
                const value = Math.round(Number(e.currentTarget.value));
                if (Number.isFinite(value)) change({ 'new.diceCount': Math.min(100, Math.max(1, value)) });
              }}
            />
          )}
        </Field>
      </Section>

      <Section title="Writing">
        <Check
          label="Check spelling as I write"
          hint="Underline misspelled words in entries, scenes and focus mode, as Realmery on the desktop does. Right-click one for suggestions, or press F7 for the next one. Grammar checking is only on the desktop, for now."
          checked={s['writing.spelling']}
          onChange={(checked) => change({ 'writing.spelling': checked })}
        />
        <Field label="Spelling" hint="The English spellings accepted: colour or color, realise or realize.">
          {(ids) => (
            <select {...ids} value={s['writing.dialect']} onChange={(e) => change({ 'writing.dialect': e.currentTarget.value as Settings['writing.dialect'] })}>
              {Object.entries(DIALECTS).map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          )}
        </Field>
        <MyDictionary />
        <Field label="Earlier versions kept per scene" hint="How many saved versions of each Story Studio scene Restore can go back to: 1 to 200.">
          {(ids) => (
            <input
              {...ids}
              type="number"
              min={1}
              max={200}
              value={s['writing.sceneRevisions']}
              onChange={(e) => {
                const value = Math.round(Number(e.currentTarget.value));
                if (Number.isFinite(value)) change({ 'writing.sceneRevisions': Math.min(200, Math.max(1, value)) });
              }}
            />
          )}
        </Field>
        <Field label="Focus mode timer pauses after" hint="Minutes without typing before the writing timer stops counting: 1 to 60.">
          {(ids) => (
            <input
              {...ids}
              type="number"
              min={1}
              max={60}
              value={s['writing.focusIdleMinutes']}
              onChange={(e) => {
                const value = Math.round(Number(e.currentTarget.value));
                if (Number.isFinite(value)) change({ 'writing.focusIdleMinutes': Math.min(60, Math.max(1, value)) });
              }}
            />
          )}
        </Field>
      </Section>

      <Section title="Atlas">
        <Field label="Measurement units" hint="Show distances, the scale bar, grid sizes, and areas in each map's own units, or converted to metric or imperial. Units made up for a world (leagues, strides) are always kept.">
          {(ids) => (
            <select {...ids} value={s['atlas.units']} onChange={(e) => change({ 'atlas.units': e.currentTarget.value as Settings['atlas.units'] })}>
              {UNIT_SYSTEMS.map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          )}
        </Field>
      </Section>

      <Section title="New things start with">
        <Field label="A new map's grid" hint="Whether a newly imported map starts with a grid.">
          {(ids) => (
            <select {...ids} value={s['new.mapGrid']} onChange={(e) => change({ 'new.mapGrid': e.currentTarget.value as Settings['new.mapGrid'] })}>
              {MAP_GRIDS.map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="A new map's scale unit" hint="The unit a new map's scale is measured in (miles, km, feet, leagues...). Empty follows Measurement units.">
          {(ids) => <input {...ids} value={s['new.mapUnit']} maxLength={40} placeholder="e.g. miles" onChange={(e) => change({ 'new.mapUnit': e.currentTarget.value })} />}
        </Field>
        <Field label="A new scene's word goal" hint="The word goal a new Story Studio scene starts with. 0 for none.">
          {(ids) => (
            <input
              {...ids}
              type="number"
              min={0}
              max={100000}
              step={100}
              value={s['new.sceneWordGoal']}
              onChange={(e) => {
                const value = Math.round(Number(e.currentTarget.value));
                if (Number.isFinite(value)) change({ 'new.sceneWordGoal': Math.min(100000, Math.max(0, value)) });
              }}
            />
          )}
        </Field>
        <Field label="A new entry's type" hint="The type New entry starts on.">
          {(ids) => (
            <select {...ids} value={s['new.entryType']} onChange={(e) => change({ 'new.entryType': e.currentTarget.value })}>
              {ENTRY_TYPES.map((type) => (
                <option key={type} value={type}>
                  {ENTITY_TYPE_NAMES[type]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="A new entry's audience" hint="Who a new entry is for, until you change it.">
          {(ids) => (
            <select {...ids} value={s['new.entryAudience']} onChange={(e) => change({ 'new.entryAudience': e.currentTarget.value })}>
              {AUDIENCES.map((audience) => (
                <option key={audience} value={audience}>
                  {AUDIENCE_NAMES[audience]}
                </option>
              ))}
            </select>
          )}
        </Field>
      </Section>

      <Section title="Keyboard shortcuts">
        <ShortcutSettings onUnsaved={() => setNotice({ kind: 'error', text: "This browser isn't keeping settings, so they'll last until this page closes." })} />
      </Section>

      <div className="actions">
        <button
          type="button"
          onClick={() => {
            change(DEFAULTS);
            setNotice({ kind: 'info', text: 'Settings are back to how Realmery comes.' });
          }}
        >
          Restore the defaults
        </button>
      </div>
    </article>
  );
}

/** Words added with "Add to My Dictionary": kept in this browser, for every realm. */
function MyDictionary() {
  useSpellingVersion();
  const words = myDictionary();
  return (
    <div className="settings-field">
      <span className="settings-label">My Dictionary</span>
      {words.length === 0 ? (
        <p className="meta">No words yet. Right-click a misspelled word you want to keep and choose Add to My Dictionary.</p>
      ) : (
        <ul className="dictionary-words" aria-label="My Dictionary">
          {words.map((word) => (
            <li key={word}>
              <span>{word}</span>
              <button type="button" className="quiet" aria-label={`Remove ${word}`} onClick={() => removeFromMyDictionary(word)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="meta">Kept in this browser, for every realm. A realm's own dictionary is on its overview, and travels with it.</p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="panel settings-section" aria-label={title}>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

/** A labelled control, with its hint as the control's description rather than part of its name. */
function Field({ label, hint, children }: { label: string; hint?: string; children: (ids: { id: string; 'aria-describedby'?: string }) => ReactNode }) {
  const id = useId();
  const hintId = useId();
  return (
    <div className="settings-field">
      <label className="settings-label" htmlFor={id}>
        {label}
      </label>
      {children(hint ? { id, 'aria-describedby': hintId } : { id })}
      {hint && (
        <span className="meta" id={hintId}>
          {hint}
        </span>
      )}
    </div>
  );
}

function Check({ label, hint, checked, onChange }: { label: string; hint: string; checked: boolean; onChange: (checked: boolean) => void }) {
  const hintId = useId();
  return (
    <label className="check">
      <input type="checkbox" checked={checked} aria-describedby={hintId} onChange={(e) => onChange(e.currentTarget.checked)} />
      <span>
        {label}
        <span className="meta" id={hintId}>
          {' '}
          {hint}
        </span>
      </span>
    </label>
  );
}
